import { useState, useCallback, useRef, useEffect } from 'react'
import { apiFetch } from '@/lib/apiFetch'
import { emitGoalBus } from '@/lib/goalBus'

export interface SessionStats {
  correct: number
  incorrect: number
  total: number
  streak: number
  avgResponseMs: number
  fastestResponseMs: number
}

interface UseExerciseSessionOptions {
  exerciseType: string
  /**
   * Close the session when this long passes with no recorded attempt. The idle
   * timer alone isn't enough for participation: a speech game keeps the mic hot,
   * so ambient noise reads as activity and the clock runs while nobody plays.
   * Real answers are the only proof, so games that want credit protected set
   * this and the server enforces the same bar on every heartbeat.
   */
  attemptTimeoutMs?: number
  onAttemptTimeout?: () => void
}

export interface SessionCredit {
  counted: boolean
  creditedMinutes: number
  attempts: number
  correct: number
  reason: string | null
}

interface PersistedSession {
  sessionId: number
  stats: SessionStats
  activeMs: number
}

const IDLE_THRESHOLD_MS = 90_000
const HEARTBEAT_INTERVAL_MS = 30_000
/** endReason reported to the server when the answers stop. */
export const NO_ATTEMPT_TIMEOUT = 'NO_ATTEMPT_TIMEOUT'

function storageKey(exerciseType: string) {
  return `exercise_session_${exerciseType}`
}

export function useExerciseSession(options: UseExerciseSessionOptions) {
  const [sessionId, setSessionId] = useState<number | null>(null)
  const [stats, setStats] = useState<SessionStats>({ correct: 0, incorrect: 0, total: 0, streak: 0, avgResponseMs: 0, fastestResponseMs: 0 })
  const [isActive, setIsActive] = useState(false)
  const [activeMs, setActiveMs] = useState(0)
  const [isIdle, setIsIdle] = useState(false)
  const [msSinceAttempt, setMsSinceAttempt] = useState(0)

  const lastActivityRef = useRef(0)
  const lastAttemptAtRef = useRef(0)
  const timedOutRef = useRef(false)
  const onAttemptTimeoutRef = useRef(options.onAttemptTimeout)
  useEffect(() => {
    onAttemptTimeoutRef.current = options.onAttemptTimeout
  }, [options.onAttemptTimeout])
  const attemptTimeoutMsRef = useRef(options.attemptTimeoutMs)
  useEffect(() => {
    attemptTimeoutMsRef.current = options.attemptTimeoutMs
  }, [options.attemptTimeoutMs])

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const activeMsRef = useRef(0)
  const responseMsTotalRef = useRef(0)
  const responseMsFastestRef = useRef(0)
  const isIdleRef = useRef(false)
  const statsRef = useRef(stats)
  const sessionIdRef = useRef<number | null>(null)

  useEffect(() => {
    statsRef.current = stats
  }, [stats])

  useEffect(() => {
    sessionIdRef.current = sessionId
  }, [sessionId])

  const persist = useCallback(() => {
    const sid = sessionIdRef.current
    if (!sid) return
    const data: PersistedSession = {
      sessionId: sid,
      stats: statsRef.current,
      activeMs: activeMsRef.current,
    }
    try {
      sessionStorage.setItem(storageKey(options.exerciseType), JSON.stringify(data))
    } catch {}
  }, [options.exerciseType])

  const clearPersisted = useCallback(() => {
    try {
      sessionStorage.removeItem(storageKey(options.exerciseType))
    } catch {}
  }, [options.exerciseType])

  const stopInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  const stopHeartbeat = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current)
      heartbeatRef.current = null
    }
  }, [])

  // Periodically tell the backend this session is still live so in-progress
  // play counts toward duration goals. Paused while the kid is idle.
  const fireAttemptTimeout = useCallback(() => {
    if (timedOutRef.current) return
    timedOutRef.current = true
    onAttemptTimeoutRef.current?.()
  }, [])

  const startHeartbeat = useCallback(() => {
    stopHeartbeat()
    heartbeatRef.current = setInterval(() => {
      const sid = sessionIdRef.current
      if (!sid || isIdleRef.current) return
      apiFetch(`/exercises/sessions/${sid}/heartbeat`, { method: 'POST' }).catch((err) => {
        // 409: the server closed the session because the answers stopped.
        if ((err as { status?: number }).status === 409) fireAttemptTimeout()
      })
    }, HEARTBEAT_INTERVAL_MS)
  }, [stopHeartbeat, fireAttemptTimeout])

  const startTimer = useCallback(() => {
    stopInterval()
    const now = Date.now()
    lastActivityRef.current = now
    // The first word gets a full window before the no-answer clock bites.
    lastAttemptAtRef.current = now
    timedOutRef.current = false
    setMsSinceAttempt(0)
    isIdleRef.current = false
    setIsIdle(false)

    intervalRef.current = setInterval(() => {
      const timeout = attemptTimeoutMsRef.current
      if (timeout) {
        const since = Date.now() - lastAttemptAtRef.current
        setMsSinceAttempt(since)
        if (since > timeout) fireAttemptTimeout()
      }

      if (!isIdleRef.current) {
        if (Date.now() - lastActivityRef.current > IDLE_THRESHOLD_MS) {
          isIdleRef.current = true
          setIsIdle(true)
        } else {
          activeMsRef.current += 1000
          setActiveMs(activeMsRef.current)
        }
      }
    }, 1000)
  }, [stopInterval, fireAttemptTimeout])

  /** Returns the new session id — callers that report a moment need it before state settles. */
  const startSession = useCallback(async (): Promise<number> => {
    const data = await apiFetch('/exercises/sessions', {
      method: 'POST',
      body: JSON.stringify({ exerciseType: options.exerciseType }),
    })
    setSessionId(data.id)
    sessionIdRef.current = data.id
    const initial: SessionStats = { correct: 0, incorrect: 0, total: 0, streak: 0, avgResponseMs: 0, fastestResponseMs: 0 }
    setStats(initial)
    statsRef.current = initial
    responseMsTotalRef.current = 0
    responseMsFastestRef.current = 0
    setIsActive(true)
    setActiveMs(0)
    activeMsRef.current = 0

    startTimer()
    startHeartbeat()
    return data.id
  }, [options.exerciseType, startTimer, startHeartbeat])

  /**
   * Ends the session and returns what it was actually worth, so the game can
   * tell the kid a round didn't count instead of it silently disappearing.
   */
  const endSession = useCallback(async (endReason?: string): Promise<SessionCredit | null> => {
    setIsActive(false)
    stopInterval()
    stopHeartbeat()
    clearPersisted()
    const sid = sessionIdRef.current
    const ms = activeMsRef.current
    const total = statsRef.current.total
    let credit: SessionCredit | null = null
    if (sid) {
      try {
        const result = await apiFetch(`/exercises/sessions/${sid}/end`, {
          method: 'POST',
          body: JSON.stringify({ activeMs: ms, ...(endReason ? { endReason } : {}) }),
        })
        credit = result?.credit ?? null
      } catch {}
      try {
        const goals = await apiFetch('/goals/report-progress', {
          method: 'POST',
          body: JSON.stringify({ sessionId: sid, activeMs: ms, exercisesCompleted: total, exerciseType: options.exerciseType }),
        })
        if (Array.isArray(goals)) emitGoalBus('goals-updated', goals)
      } catch {}
    }
    return credit
  }, [stopInterval, stopHeartbeat, clearPersisted])

  const recordAttempt = useCallback(async (
    wordId: number | null,
    correct: boolean,
    responseMs: number,
    attemptDetail?: object,
  ) => {
    lastActivityRef.current = Date.now()
    lastAttemptAtRef.current = Date.now()
    setMsSinceAttempt(0)
    if (isIdleRef.current) {
      isIdleRef.current = false
      setIsIdle(false)
    }

    responseMsTotalRef.current += responseMs
    if (responseMsFastestRef.current === 0 || responseMs < responseMsFastestRef.current) {
      responseMsFastestRef.current = responseMs
    }
    const newTotal = statsRef.current.total + 1
    const newStats: SessionStats = {
      correct: statsRef.current.correct + (correct ? 1 : 0),
      incorrect: statsRef.current.incorrect + (correct ? 0 : 1),
      total: newTotal,
      streak: correct ? statsRef.current.streak + 1 : 0,
      avgResponseMs: Math.round(responseMsTotalRef.current / newTotal),
      fastestResponseMs: responseMsFastestRef.current,
    }
    setStats(newStats)
    statsRef.current = newStats

    persist()

    const sid = sessionIdRef.current
    if (!sid) return null

    try {
      return await apiFetch(`/exercises/sessions/${sid}/attempts`, {
        method: 'POST',
        body: JSON.stringify({
          wordId,
          correct,
          responseMs,
          ...(attemptDetail ? { attemptDetail: JSON.stringify(attemptDetail) } : {}),
        }),
      })
    } catch {
      return null
    }
  }, [persist])

  const touchActivity = useCallback(() => {
    lastActivityRef.current = Date.now()
    if (isIdleRef.current) {
      isIdleRef.current = false
      setIsIdle(false)
    }
  }, [])

  useEffect(() => {
    if (!isActive) return
    const events = ['pointerdown', 'keydown', 'touchstart', 'scroll'] as const
    events.forEach(e => window.addEventListener(e, touchActivity, { passive: true }))
    return () => {
      events.forEach(e => window.removeEventListener(e, touchActivity))
    }
  }, [isActive, touchActivity])

  const dismissIdle = useCallback(() => {
    lastActivityRef.current = Date.now()
    isIdleRef.current = false
    setIsIdle(false)
  }, [])

  const restoreSession = useCallback((): PersistedSession | null => {
    try {
      const raw = sessionStorage.getItem(storageKey(options.exerciseType))
      if (!raw) return null
      const data: PersistedSession = JSON.parse(raw)
      if (!data.sessionId) return null

      setSessionId(data.sessionId)
      sessionIdRef.current = data.sessionId
      setStats(data.stats)
      statsRef.current = data.stats
      setActiveMs(data.activeMs)
      activeMsRef.current = data.activeMs
      setIsActive(true)

      startTimer()
      startHeartbeat()

      return data
    } catch {
      return null
    }
  }, [options.exerciseType, startTimer])

  // Let the goal progress bar (mounted in the Layout shell) tick in real time
  // while a session is running and pause when the kid goes idle.
  useEffect(() => {
    emitGoalBus('session-state', { active: isActive, idle: isIdle })
  }, [isActive, isIdle])

  useEffect(() => {
    return () => {
      stopInterval()
      stopHeartbeat()
      emitGoalBus('session-state', { active: false, idle: false })
    }
  }, [stopInterval, stopHeartbeat])

  return {
    sessionId,
    stats,
    isActive,
    activeMs,
    isIdle,
    msSinceAttempt,
    startSession,
    endSession,
    recordAttempt,
    dismissIdle,
    touchActivity,
    restoreSession,
    persist,
    clearPersisted,
  }
}
